import { useEffect, useRef, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { setSiteConfig } from "@/hooks/useSiteConfig";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, IndianRupee, LayoutTemplate, GripVertical, Video, Star, MessageSquareQuote, Clapperboard, ImageIcon, Users } from "lucide-react";
import { STOREFRONT_THEMES } from "@/lib/storefrontTheme";
import defaultLogo from "@/assets/logo.png";
import { BRAND_LOGO_MIN, BRAND_LOGO_MAX, BRAND_LOGO_HEADER_DEFAULT, BRAND_LOGO_FOOTER_DEFAULT, BRAND_LOGO_MOBILE_DEFAULT, clampBrandLogoHeight } from "@/components/Brand";

const emptyItem = () => ({ id: "th_" + Math.random().toString(16).slice(2, 10), image_url: "", title: "", description: "", url: "", storefront_theme: "classic" });
const emptyVideo = () => ({ id: "vt_" + Math.random().toString(16).slice(2, 10), name: "", role: "", video_url: "", thumbnail_url: "" });
const emptyReview = () => ({ id: "tt_" + Math.random().toString(16).slice(2, 10), title: "", image_url: "", rating: 5, text: "" });

function padHeroAvatars(list) {
  const src = Array.isArray(list) ? list : [];
  return [0, 1, 2, 3].map((i) => String(src[i] || ""));
}

function hydrateBrand(raw) {
  const merged = {
    logo_url: "",
    favicon_url: "",
    site_title: "",
    tagline: "",
    logo_height_header: BRAND_LOGO_HEADER_DEFAULT,
    logo_height_footer: BRAND_LOGO_FOOTER_DEFAULT,
    logo_height_mobile: BRAND_LOGO_MOBILE_DEFAULT,
    hero_avatars: ["", "", "", ""],
    ...(raw || {}),
  };
  merged.hero_avatars = padHeroAvatars(merged.hero_avatars);
  return merged;
}

const SOCIAL_FIELDS = [
  { key: "x", label: "X (Twitter)" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "youtube", label: "YouTube" },
];

function emptyVenture() {
  return { name: "", url: "" };
}

function PersonFields({ roleKey, person, onChange, uploadFor, uploadingKey, fileRefs }) {
  const social = person.social || {};
  const ventures = Array.isArray(person.ventures) ? person.ventures : [];
  const setSocial = (key, value) => onChange({ social: { ...social, [key]: value } });
  const setVenture = (i, patch) => {
    const next = ventures.map((v, idx) => (idx === i ? { ...v, ...patch } : v));
    onChange({ ventures: next });
  };
  return (
    <Card className="bg-slate-900 border-slate-800 p-5 space-y-4" data-testid={`company-editor-${roleKey}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div>
          <Label className="text-slate-100">Profile photo (circle)</Label>
          <div className="mt-1.5 h-24 w-24 rounded-full overflow-hidden bg-white border border-slate-700 flex items-center justify-center">
            {person.photo_url
              ? <img src={mediaUrl(person.photo_url)} alt="" className="h-full w-full object-cover" data-testid={`${roleKey}-photo-preview`} />
              : <span className="text-[10px] text-slate-400 px-2 text-center">No photo</span>}
          </div>
          <input ref={(el) => (fileRefs.current[`${roleKey}-photo`] = el)} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFor(`${roleKey}-photo`, e.target.files[0], (url) => onChange({ photo_url: url }))} />
          <div className="mt-2 flex gap-2">
            <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 h-8 text-xs" onClick={() => fileRefs.current[`${roleKey}-photo`]?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1" /> {uploadingKey === `${roleKey}-photo` ? "…" : "Upload"}
            </Button>
            {person.photo_url ? (
              <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-red-300 hover:bg-slate-700 h-8 text-xs" onClick={() => onChange({ photo_url: "" })}>Remove</Button>
            ) : null}
          </div>
          <p className="text-xs text-slate-500 mt-2 max-w-[16rem]">Recommended: square 800×800 px (1:1) or 1200×1200, face centered, JPG/PNG under 5 MB. Shown as a circle on the public page.</p>
        </div>
        <div>
          <Label className="text-slate-100">Banner / background</Label>
          <div className="mt-1.5 h-24 w-44 rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center">
            {person.banner_url
              ? <img src={mediaUrl(person.banner_url)} alt="" className="h-full w-full object-cover" data-testid={`${roleKey}-banner-preview`} />
              : <span className="text-[10px] text-slate-400 px-2 text-center">No banner</span>}
          </div>
          <input ref={(el) => (fileRefs.current[`${roleKey}-banner`] = el)} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFor(`${roleKey}-banner`, e.target.files[0], (url) => onChange({ banner_url: url }))} />
          <div className="mt-2 flex gap-2">
            <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 h-8 text-xs" onClick={() => fileRefs.current[`${roleKey}-banner`]?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1" /> {uploadingKey === `${roleKey}-banner` ? "…" : "Upload"}
            </Button>
            {person.banner_url ? (
              <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-red-300 hover:bg-slate-700 h-8 text-xs" onClick={() => onChange({ banner_url: "" })}>Remove</Button>
            ) : null}
          </div>
          <p className="text-xs text-slate-500 mt-2 max-w-[16rem]">Recommended: 1600×500 px (about 16:5), JPG/PNG under 5 MB. Faces work best on the right third of the image.</p>
        </div>
        <div className="flex-1 min-w-[16rem] grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-100">Name</Label>
            <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={person.name || ""} onChange={(e) => onChange({ name: e.target.value })} data-testid={`${roleKey}-name`} />
          </div>
          <div>
            <Label className="text-slate-100">Legal name</Label>
            <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={person.legal_name || ""} onChange={(e) => onChange({ legal_name: e.target.value })} data-testid={`${roleKey}-legal`} placeholder="Optional" />
          </div>
          <div>
            <Label className="text-slate-100">Role label</Label>
            <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={person.role || ""} onChange={(e) => onChange({ role: e.target.value })} data-testid={`${roleKey}-role`} />
          </div>
          <div>
            <Label className="text-slate-100">Contact email</Label>
            <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={person.email || ""} onChange={(e) => onChange({ email: e.target.value })} data-testid={`${roleKey}-email`} />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-slate-100">Headline</Label>
            <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={person.headline || ""} onChange={(e) => onChange({ headline: e.target.value })} data-testid={`${roleKey}-headline`} placeholder="One line under the name and on the banner" />
          </div>
        </div>
      </div>
      <div>
        <Label className="text-slate-100">Biography</Label>
        <Textarea className="mt-1.5 bg-slate-800 border-slate-700 text-white" rows={7} value={person.bio || ""} onChange={(e) => onChange({ bio: e.target.value })} data-testid={`${roleKey}-bio`} />
        <p className="text-xs text-slate-500 mt-1">Shown as the main paragraph on the public page. Use line breaks for new paragraphs.</p>
      </div>
      <div>
        <Label className="text-slate-100">Social profiles</Label>
        <div className="mt-2 grid sm:grid-cols-2 gap-3">
          {SOCIAL_FIELDS.map((s) => (
            <div key={s.key}>
              <Label className="text-slate-400 text-xs">{s.label}</Label>
              <Input className="mt-1 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" placeholder="https://…" value={social[s.key] || ""} onChange={(e) => setSocial(s.key, e.target.value)} data-testid={`${roleKey}-social-${s.key}`} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-slate-100">{person.role || "Founder"} of — each name + link</Label>
          <Button type="button" size="sm" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 h-8" onClick={() => onChange({ ventures: [...ventures, emptyVenture()] })} data-testid={`${roleKey}-venture-add`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        <div className="mt-2 space-y-2">
          {ventures.map((v, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2">
              <Input className="bg-slate-800 border-slate-700 text-white" placeholder="Company name" value={v.name || ""} onChange={(e) => setVenture(i, { name: e.target.value })} data-testid={`${roleKey}-venture-name-${i}`} />
              <Input className="bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" placeholder="https://…" value={v.url || ""} onChange={(e) => setVenture(i, { url: e.target.value })} data-testid={`${roleKey}-venture-url-${i}`} />
              <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-red-300" onClick={() => onChange({ ventures: ventures.filter((_, idx) => idx !== i) })}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

export default function SuperLanding() {
  const [pricing, setPricing] = useState(null);
  const [brand, setBrand] = useState(null);
  const [themes, setThemes] = useState(null);
  const [intro, setIntro] = useState(null);
  const [videoT, setVideoT] = useState(null);
  const [textT, setTextT] = useState(null);
  const [people, setPeople] = useState(null);
  const [tab, setTab] = useState("home");
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    try {
      const { data } = await api.get("/platform/landing");
      setBrand(hydrateBrand(data.brand));
      setPricing(data.pricing);
      setThemes(data.theme_showcase);
      setIntro(data.intro_video);
      setVideoT(data.video_testimonials);
      setTextT(data.text_testimonials);
      setPeople(data.company_people || { founder: {}, co_founder: {} });
      setSiteConfig(data);
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // features come from a textarea (one per line) held in plan._featuresText when edited
      const plans = (pricing.plans || []).map((p) => ({
        ...p,
        price: Number(p.price) || 0,
        features: typeof p._featuresText === "string"
          ? p._featuresText.split("\n").map((s) => s.trim()).filter(Boolean)
          : (p.features || []),
      }));
      plans.forEach((p) => delete p._featuresText);
      const payload = {
        brand: hydrateBrand(brand),
        pricing: { ...pricing, plans },
        theme_showcase: themes,
        intro_video: intro,
        video_testimonials: videoT,
        text_testimonials: textT,
        company_people: people,
      };
      const { data } = await api.put("/platform/landing", payload);
      setBrand(hydrateBrand(data.brand));
      setPricing(data.pricing);
      setThemes(data.theme_showcase);
      setIntro(data.intro_video);
      setVideoT(data.video_testimonials);
      setTextT(data.text_testimonials);
      setPeople(data.company_people || people);
      setSiteConfig(data);
      toast.success("Landing content saved");
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };

  const setPlan = (i, patch) => {
    const plans = [...pricing.plans];
    plans[i] = { ...plans[i], ...patch };
    setPricing({ ...pricing, plans });
  };

  const setItem = (i, patch) => {
    const items = [...(themes.items || [])];
    items[i] = { ...items[i], ...patch };
    setThemes({ ...themes, items });
  };
  const addItem = () => setThemes({ ...themes, items: [...(themes.items || []), emptyItem()] });
  const removeItem = (i) => setThemes({ ...themes, items: themes.items.filter((_, idx) => idx !== i) });
  const moveItem = (i, dir) => {
    const items = [...themes.items];
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    [items[i], items[j]] = [items[j], items[i]];
    setThemes({ ...themes, items });
  };

  // Generic list helpers for video / review sections
  const listSet = (section, setter, i, patch) => {
    const items = [...(section.items || [])];
    items[i] = { ...items[i], ...patch };
    setter({ ...section, items });
  };
  const listAdd = (section, setter, factory) => setter({ ...section, items: [...(section.items || []), factory()] });
  const listRemove = (section, setter, i) => setter({ ...section, items: section.items.filter((_, idx) => idx !== i) });

  // Generic upload: key is a unique string; onUrl receives the resulting url.
  const uploadFor = async (key, file, onUrl) => {
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/platform/landing/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUrl(data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(extractErr(e)); } finally { setUploadingKey(null); }
  };
  const uploadImage = (i, file) => uploadFor(`theme-${i}`, file, (url) => setItem(i, { image_url: url }));

  if (!pricing || !themes || !intro || !videoT || !textT || !brand || !people) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8 max-w-4xl pb-24">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Landing page</h1>
          <p className="text-slate-500 text-sm mt-1">Edit the public homepage and company founder pages. Changes go live instantly.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="landing-save-top" className="bg-indigo-500 hover:bg-indigo-600 shrink-0">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="flex gap-2 border-b border-slate-800 pb-px" data-testid="landing-tabs">
        {[
          { id: "home", label: "Homepage" },
          { id: "founders", label: "Company Founder" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg ${tab === t.id ? "bg-slate-900 text-white border border-slate-700 border-b-slate-900 -mb-px" : "text-slate-400 hover:text-white"}`}
            data-testid={`landing-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "founders" ? (
        <div className="space-y-8">
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-indigo-300">
              <Users className="w-5 h-5" />
              <h2 className="font-display text-lg font-bold text-slate-100">Founder <span className="text-slate-500 font-normal text-sm">/company/founder</span></h2>
            </div>
            <PersonFields
              roleKey="founder"
              person={people.founder || {}}
              onChange={(patch) => setPeople({ ...people, founder: { ...(people.founder || {}), ...patch } })}
              uploadFor={uploadFor}
              uploadingKey={uploadingKey}
              fileRefs={fileRefs}
            />
          </section>
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-indigo-300">
              <Users className="w-5 h-5" />
              <h2 className="font-display text-lg font-bold text-slate-100">Co-founder <span className="text-slate-500 font-normal text-sm">/company/co-founder</span></h2>
            </div>
            <PersonFields
              roleKey="cofounder"
              person={people.co_founder || {}}
              onChange={(patch) => setPeople({ ...people, co_founder: { ...(people.co_founder || {}), ...patch } })}
              uploadFor={uploadFor}
              uploadingKey={uploadingKey}
              fileRefs={fileRefs}
            />
          </section>
        </div>
      ) : null}

      {tab === "home" ? (
      <>
      {/* BRAND IDENTITY */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <ImageIcon className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Brand identity <span className="text-slate-500 font-normal text-sm">(tab title, tagline, favicon, logo)</span></h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-100">Site title</Label>
              <Input
                className="mt-1.5 bg-slate-800 border-slate-700 text-white"
                placeholder="Mera Startup"
                value={brand.site_title || ""}
                onChange={(e) => setBrand({ ...brand, site_title: e.target.value })}
                data-testid="brand-site-title"
              />
              <p className="text-xs text-slate-500 mt-1.5">Browser tab title and Open Graph site name. Not shown next to the logo.</p>
            </div>
            <div>
              <Label className="text-slate-100">Tagline</Label>
              <Input
                className="mt-1.5 bg-slate-800 border-slate-700 text-white"
                placeholder="India's Best Shopify Alternative"
                value={brand.tagline || ""}
                onChange={(e) => setBrand({ ...brand, tagline: e.target.value })}
                data-testid="brand-tagline"
              />
              <p className="text-xs text-slate-500 mt-1.5">Used as the meta description when SEO description is empty. Not shown in the header.</p>
            </div>
          </div>

          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <Label className="text-slate-100">Favicon</Label>
              <div className="mt-1.5 h-16 w-16 rounded-lg overflow-hidden bg-white border border-slate-700 flex items-center justify-center">
                {brand.favicon_url
                  ? <img src={mediaUrl(brand.favicon_url)} alt="Favicon" className="h-10 w-10 object-contain" data-testid="brand-favicon-preview" />
                  : <span className="text-[10px] text-slate-400 text-center px-1">None</span>}
              </div>
              <input ref={(el) => (fileRefs.current["favicon"] = el)} type="file" accept="image/png,image/x-icon,image/svg+xml,image/webp,image/jpeg" hidden onChange={(e) => e.target.files?.[0] && uploadFor("favicon", e.target.files[0], (url) => setBrand({ ...brand, favicon_url: url }))} data-testid="brand-favicon-file" />
              <div className="mt-2 flex gap-2">
                <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" onClick={() => fileRefs.current["favicon"]?.click()} data-testid="brand-favicon-upload">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploadingKey === "favicon" ? "Uploading…" : "Upload favicon"}
                </Button>
                {brand.favicon_url ? (
                  <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-red-300 hover:bg-slate-700" onClick={() => setBrand({ ...brand, favicon_url: "" })} data-testid="brand-favicon-reset">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex-1 min-w-0 sm:min-w-[16rem]">
              <Label className="text-slate-100">Favicon URL <span className="text-slate-500 font-normal">(or use Upload)</span></Label>
              <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" placeholder="https://…/favicon.png" value={brand.favicon_url || ""} onChange={(e) => setBrand({ ...brand, favicon_url: e.target.value })} data-testid="brand-favicon-url" />
              <p className="text-xs text-slate-500 mt-2">Square PNG or ICO, 32×32 or 64×64 works well.</p>
            </div>
          </div>

          <div className="flex items-end gap-4 flex-wrap pt-2 border-t border-slate-800">
            <div>
              <Label className="text-slate-100">Header logo</Label>
              <div className="mt-1.5 h-20 min-w-[12rem] px-4 rounded-lg overflow-hidden bg-white border border-slate-700 flex items-center justify-center">
                {brand.logo_url
                  ? <img src={mediaUrl(brand.logo_url)} alt="Landing logo" className="h-12 w-auto object-contain" data-testid="brand-logo-preview" />
                  : <span className="text-xs text-slate-400">Default MeraStartup logo</span>}
              </div>
              <input ref={(el) => (fileRefs.current["brand"] = el)} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFor("brand", e.target.files[0], (url) => setBrand({ ...brand, logo_url: url }))} data-testid="brand-file" />
              <div className="mt-2 flex gap-2">
                <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" onClick={() => fileRefs.current["brand"]?.click()} data-testid="brand-upload">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploadingKey === "brand" ? "Uploading…" : "Upload logo"}
                </Button>
                {brand.logo_url ? (
                  <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-red-300 hover:bg-slate-700" onClick={() => setBrand({ ...brand, logo_url: "" })} data-testid="brand-reset">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Reset to default
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex-1 min-w-0 sm:min-w-[16rem]">
              <Label className="text-slate-100">Logo URL <span className="text-slate-500 font-normal">(or use Upload)</span></Label>
              <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" placeholder="Leave empty for the default logo" value={brand.logo_url || ""} onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} data-testid="brand-logo-url" />
              <p className="text-xs text-slate-500 mt-2">Tip: a transparent PNG works best. Remember to click “Save changes”.</p>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-800 space-y-4">
            <p className="text-sm text-slate-300 font-medium">Logo size on the public site</p>
            <p className="text-xs text-slate-500">Header, footer, and mobile header each have their own height. Range {BRAND_LOGO_MIN}–{BRAND_LOGO_MAX}px.</p>
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                { key: "logo_height_header", label: "Header (desktop)", test: "header", fallback: BRAND_LOGO_HEADER_DEFAULT },
                { key: "logo_height_footer", label: "Footer", test: "footer", fallback: BRAND_LOGO_FOOTER_DEFAULT },
                { key: "logo_height_mobile", label: "Mobile header", test: "mobile", fallback: BRAND_LOGO_MOBILE_DEFAULT },
              ].map((row) => {
                const val = clampBrandLogoHeight(brand[row.key], row.fallback);
                return (
                  <div key={row.key}>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-slate-100">{row.label}</Label>
                      <span className="text-xs font-mono-nums text-slate-400" data-testid={`brand-logo-h-${row.test}-val`}>{val}px</span>
                    </div>
                    <Slider
                      min={BRAND_LOGO_MIN}
                      max={BRAND_LOGO_MAX}
                      step={1}
                      value={[val]}
                      onValueChange={(v) => setBrand({ ...brand, [row.key]: v[0] })}
                      data-testid={`brand-logo-h-${row.test}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { label: "Header", h: clampBrandLogoHeight(brand.logo_height_header, BRAND_LOGO_HEADER_DEFAULT), test: "header" },
                { label: "Footer", h: clampBrandLogoHeight(brand.logo_height_footer, BRAND_LOGO_FOOTER_DEFAULT), test: "footer" },
                { label: "Mobile", h: clampBrandLogoHeight(brand.logo_height_mobile, BRAND_LOGO_MOBILE_DEFAULT), test: "mobile" },
              ].map((p) => (
                <div key={p.test} className="rounded-lg border border-slate-700 bg-white p-3 flex flex-col items-start justify-center min-h-[5rem]">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{p.label} preview</div>
                  <img
                    src={brand.logo_url ? mediaUrl(brand.logo_url) : defaultLogo}
                    alt=""
                    style={{ height: p.h }}
                    className="w-auto max-w-full object-contain"
                    data-testid={`brand-logo-preview-${p.test}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-800 space-y-3">
            <p className="text-sm text-slate-300 font-medium">Hero photos (A–D)</p>
            <p className="text-xs text-slate-500">These four circular photos sit under the hero buttons (the A / B / C / D stack). Square images work best.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {padHeroAvatars(brand.hero_avatars).map((url, i) => (
                <div key={i} className="space-y-2">
                  <Label className="text-slate-100">Photo {["A", "B", "C", "D"][i]}</Label>
                  <div className="h-16 w-16 rounded-full overflow-hidden bg-white border border-slate-700 flex items-center justify-center">
                    {url
                      ? <img src={mediaUrl(url)} alt="" className="h-full w-full object-cover" data-testid={`hero-avatar-preview-${i}`} />
                      : <span className="text-sm font-bold text-slate-400">{["A", "B", "C", "D"][i]}</span>}
                  </div>
                  <input
                    ref={(el) => (fileRefs.current[`hero-avatar-${i}`] = el)}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => e.target.files?.[0] && uploadFor(`hero-avatar-${i}`, e.target.files[0], (next) => {
                      const avatars = padHeroAvatars(brand.hero_avatars);
                      avatars[i] = next;
                      setBrand({ ...brand, hero_avatars: avatars });
                    })}
                    data-testid={`hero-avatar-file-${i}`}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    <Button type="button" size="sm" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 h-8 px-2 text-xs" onClick={() => fileRefs.current[`hero-avatar-${i}`]?.click()} data-testid={`hero-avatar-upload-${i}`}>
                      <Upload className="w-3 h-3 mr-1" /> {uploadingKey === `hero-avatar-${i}` ? "…" : "Upload"}
                    </Button>
                    {url ? (
                      <Button type="button" size="sm" variant="outline" className="border-slate-700 bg-slate-800 text-red-300 hover:bg-slate-700 h-8 px-2 text-xs" onClick={() => {
                        const avatars = padHeroAvatars(brand.hero_avatars);
                        avatars[i] = "";
                        setBrand({ ...brand, hero_avatars: avatars });
                      }} data-testid={`hero-avatar-reset-${i}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </section>

      {/* PRICING */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <IndianRupee className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Pricing section</h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <div>
            <Label className="text-slate-100">Section heading</Label>
            <Input data-testid="pricing-heading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={pricing.heading || ""} onChange={(e) => setPricing({ ...pricing, heading: e.target.value })} />
          </div>
          <div>
            <Label className="text-slate-100">Section subheading</Label>
            <Textarea data-testid="pricing-subheading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" rows={2} value={pricing.subheading || ""} onChange={(e) => setPricing({ ...pricing, subheading: e.target.value })} />
          </div>
        </Card>

        {(pricing.plans || []).map((p, i) => (
          <Card key={p.id || i} className="bg-slate-900 border-slate-800 p-5 space-y-4" data-testid={`plan-card-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Plan {i + 1} · <span className="text-slate-300 font-mono-nums">{p.id}</span></span>
              <label className="flex items-center gap-2 text-xs text-slate-300">
                Highlight
                <Switch checked={!!p.highlight} onCheckedChange={(v) => setPlan(i, { highlight: v })} data-testid={`plan-highlight-${i}`} />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-100">Plan name</Label>
                <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={p.name || ""} onChange={(e) => setPlan(i, { name: e.target.value })} data-testid={`plan-name-${i}`} />
              </div>
              <div>
                <Label className="text-slate-100">Badge (optional)</Label>
                <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" placeholder="e.g. Best value" value={p.badge || ""} onChange={(e) => setPlan(i, { badge: e.target.value })} data-testid={`plan-badge-${i}`} />
              </div>
              <div>
                <Label className="text-slate-100">Price (₹ per period)</Label>
                <Input type="number" className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums" value={p.price ?? 0} onChange={(e) => setPlan(i, { price: e.target.value })} data-testid={`plan-price-${i}`} />
              </div>
              <div>
                <Label className="text-slate-100">Period</Label>
                <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" placeholder="year" value={p.period || ""} onChange={(e) => setPlan(i, { period: e.target.value })} data-testid={`plan-period-${i}`} />
                <p className="text-[11px] text-slate-500 mt-1">Public landing shows yearly plans only. Use <span className="font-mono-nums">year</span> — monthly rows are hidden.</p>
              </div>
            </div>
            <div>
              <Label className="text-slate-100">Tagline</Label>
              <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={p.tagline || ""} onChange={(e) => setPlan(i, { tagline: e.target.value })} data-testid={`plan-tagline-${i}`} />
            </div>
            <div>
              <Label className="text-slate-100">Features / pricing table text <span className="text-slate-500 font-normal">(one per line)</span></Label>
              <Textarea
                className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-sm"
                rows={6}
                value={typeof p._featuresText === "string" ? p._featuresText : (p.features || []).join("\n")}
                onChange={(e) => setPlan(i, { _featuresText: e.target.value })}
                data-testid={`plan-features-${i}`}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-100">CTA button text</Label>
                <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={p.cta_text || ""} onChange={(e) => setPlan(i, { cta_text: e.target.value })} data-testid={`plan-cta-${i}`} />
              </div>
              <div>
                <Label className="text-slate-100">Footnote (optional)</Label>
                <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={p.footnote || ""} onChange={(e) => setPlan(i, { footnote: e.target.value })} data-testid={`plan-footnote-${i}`} />
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* THEME SHOWCASE */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <LayoutTemplate className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Theme showcase</h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <div>
            <Label className="text-slate-100">Section heading</Label>
            <Input data-testid="themes-heading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={themes.heading || ""} onChange={(e) => setThemes({ ...themes, heading: e.target.value })} />
          </div>
          <div>
            <Label className="text-slate-100">Section subheading</Label>
            <Textarea data-testid="themes-subheading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" rows={2} value={themes.subheading || ""} onChange={(e) => setThemes({ ...themes, subheading: e.target.value })} />
          </div>
        </Card>

        {(themes.items || []).map((it, i) => (
          <Card key={it.id || i} className="bg-slate-900 border-slate-800 p-5" data-testid={`theme-item-${i}`}>
            <div className="flex gap-4">
              {/* image */}
              <div className="w-40 shrink-0">
                <div className="aspect-video rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center">
                  {it.image_url ? <img src={mediaUrl(it.image_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-slate-500">No image</span>}
                </div>
                <input ref={(el) => (fileRefs.current[`theme-${i}`] = el)} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadImage(i, e.target.files[0])} data-testid={`theme-file-${i}`} />
                <Button type="button" variant="outline" className="mt-2 w-full border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" onClick={() => fileRefs.current[`theme-${i}`]?.click()} data-testid={`theme-upload-${i}`}>
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploadingKey === `theme-${i}` ? "Uploading…" : "Upload"}
                </Button>
              </div>
              {/* fields */}
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-slate-500">
                    <button type="button" onClick={() => moveItem(i, -1)} className="hover:text-slate-200" data-testid={`theme-up-${i}`}><GripVertical className="w-4 h-4" /></button>
                    <span className="text-xs uppercase tracking-wider">Theme {i + 1}</span>
                  </div>
                  <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-300 text-sm inline-flex items-center gap-1" data-testid={`theme-remove-${i}`}><Trash2 className="w-4 h-4" /> Remove</button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-100">Title</Label>
                    <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={it.title || ""} onChange={(e) => setItem(i, { title: e.target.value })} data-testid={`theme-title-${i}`} />
                  </div>
                  <div>
                    <Label className="text-slate-100">Link URL</Label>
                    <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" placeholder="/fresh-fruits or https://…" value={it.url || ""} onChange={(e) => setItem(i, { url: e.target.value })} data-testid={`theme-url-${i}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-100">Description</Label>
                  <Textarea className="mt-1.5 bg-slate-800 border-slate-700 text-white" rows={2} value={it.description || ""} onChange={(e) => setItem(i, { description: e.target.value })} data-testid={`theme-desc-${i}`} />
                </div>
                <div>
                  <Label className="text-slate-100">Image URL <span className="text-slate-500 font-normal">(or use Upload)</span></Label>
                  <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" value={it.image_url || ""} onChange={(e) => setItem(i, { image_url: e.target.value })} data-testid={`theme-imgurl-${i}`} />
                </div>
                <div>
                  <Label className="text-slate-100">Storefront (Classic, Modern, Trendy, Perfecto, Boxy, Medico, Mixy, Various, Beast, Locally, QuickSto or BigShow)</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">Create Store on the homepage starts signup with this look.</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {STOREFRONT_THEMES.map((th) => {
                      const tid = th.id;
                      const sel = (it.storefront_theme || "classic") === tid;
                      return (
                        <button
                          key={tid}
                          type="button"
                          onClick={() => setItem(i, { storefront_theme: tid })}
                          data-testid={`theme-kind-${i}-${tid}`}
                          className={`px-3 py-1.5 rounded-full text-sm font-semibold border capitalize ${sel ? "bg-indigo-600 border-indigo-400 text-white" : "border-slate-600 text-slate-300 hover:border-slate-400"}`}
                        >
                          {th.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}

        <Button type="button" variant="outline" onClick={addItem} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" data-testid="theme-add">
          <Plus className="w-4 h-4 mr-1.5" /> Add theme
        </Button>
      </section>

      {/* INTRO VIDEO (16:9) */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <Clapperboard className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">&quot;What we&apos;re about&quot; intro video <span className="text-slate-500 font-normal text-sm">(plays in the public hero, right side)</span></h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-100">Heading</Label>
              <Input data-testid="intro-heading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={intro.heading || ""} onChange={(e) => setIntro({ ...intro, heading: e.target.value })} />
            </div>
            <div>
              <Label className="text-slate-100">Video URL <span className="text-slate-500 font-normal">(YouTube, Vimeo or .mp4)</span></Label>
              <Input data-testid="intro-video-url" className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" value={intro.video_url || ""} onChange={(e) => setIntro({ ...intro, video_url: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-slate-100">Subheading</Label>
            <Textarea data-testid="intro-subheading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" rows={2} value={intro.subheading || ""} onChange={(e) => setIntro({ ...intro, subheading: e.target.value })} />
          </div>
          <div className="flex items-end gap-4">
            <div className="w-48">
              <Label className="text-slate-100">Thumbnail</Label>
              <div className="mt-1.5 aspect-video rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center">
                {intro.thumbnail_url ? <img src={mediaUrl(intro.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-slate-500">No thumbnail</span>}
              </div>
              <input ref={(el) => (fileRefs.current["intro"] = el)} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFor("intro", e.target.files[0], (url) => setIntro({ ...intro, thumbnail_url: url }))} data-testid="intro-file" />
              <Button type="button" variant="outline" className="mt-2 w-full border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" onClick={() => fileRefs.current["intro"]?.click()} data-testid="intro-upload">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploadingKey === "intro" ? "Uploading…" : "Upload thumbnail"}
              </Button>
            </div>
            <div className="flex-1">
              <Label className="text-slate-100">Thumbnail URL</Label>
              <Input data-testid="intro-thumb-url" className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" value={intro.thumbnail_url || ""} onChange={(e) => setIntro({ ...intro, thumbnail_url: e.target.value })} />
            </div>
          </div>
        </Card>
      </section>

      {/* VIDEO TESTIMONIALS (9:16) */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <Video className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Video testimonials <span className="text-slate-500 font-normal text-sm">(9:16)</span></h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <div>
            <Label className="text-slate-100">Section heading</Label>
            <Input data-testid="videot-heading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={videoT.heading || ""} onChange={(e) => setVideoT({ ...videoT, heading: e.target.value })} />
          </div>
          <div>
            <Label className="text-slate-100">Section subheading</Label>
            <Textarea data-testid="videot-subheading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" rows={2} value={videoT.subheading || ""} onChange={(e) => setVideoT({ ...videoT, subheading: e.target.value })} />
          </div>
        </Card>

        {(videoT.items || []).map((v, i) => (
          <Card key={v.id || i} className="bg-slate-900 border-slate-800 p-5" data-testid={`videot-item-${i}`}>
            <div className="flex gap-4">
              <div className="w-28 shrink-0">
                <div className="aspect-[9/16] rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center">
                  {v.thumbnail_url ? <img src={mediaUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-500 text-center px-1">No thumb</span>}
                </div>
                <input ref={(el) => (fileRefs.current[`vt-${i}`] = el)} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFor(`vt-${i}`, e.target.files[0], (url) => listSet(videoT, setVideoT, i, { thumbnail_url: url }))} data-testid={`videot-file-${i}`} />
                <Button type="button" variant="outline" className="mt-2 w-full border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs px-1" onClick={() => fileRefs.current[`vt-${i}`]?.click()} data-testid={`videot-upload-${i}`}>
                  <Upload className="w-3.5 h-3.5 mr-1" /> {uploadingKey === `vt-${i}` ? "…" : "Thumb"}
                </Button>
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Video {i + 1}</span>
                  <button type="button" onClick={() => listRemove(videoT, setVideoT, i)} className="text-red-400 hover:text-red-300 text-sm inline-flex items-center gap-1" data-testid={`videot-remove-${i}`}><Trash2 className="w-4 h-4" /> Remove</button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-100">Name</Label>
                    <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={v.name || ""} onChange={(e) => listSet(videoT, setVideoT, i, { name: e.target.value })} data-testid={`videot-name-${i}`} />
                  </div>
                  <div>
                    <Label className="text-slate-100">Role / store</Label>
                    <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={v.role || ""} onChange={(e) => listSet(videoT, setVideoT, i, { role: e.target.value })} data-testid={`videot-role-${i}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-100">Video URL <span className="text-slate-500 font-normal">(YouTube, Vimeo or .mp4)</span></Label>
                  <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" value={v.video_url || ""} onChange={(e) => listSet(videoT, setVideoT, i, { video_url: e.target.value })} data-testid={`videot-url-${i}`} />
                </div>
                <div>
                  <Label className="text-slate-100">Thumbnail URL</Label>
                  <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" value={v.thumbnail_url || ""} onChange={(e) => listSet(videoT, setVideoT, i, { thumbnail_url: e.target.value })} data-testid={`videot-thumburl-${i}`} />
                </div>
              </div>
            </div>
          </Card>
        ))}
        <Button type="button" variant="outline" onClick={() => listAdd(videoT, setVideoT, emptyVideo)} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" data-testid="videot-add">
          <Plus className="w-4 h-4 mr-1.5" /> Add video testimonial
        </Button>
      </section>

      {/* TEXT TESTIMONIALS (Google review style) */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <MessageSquareQuote className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Text testimonials <span className="text-slate-500 font-normal text-sm">(review cards)</span></h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <div>
            <Label className="text-slate-100">Section heading</Label>
            <Input data-testid="textt-heading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={textT.heading || ""} onChange={(e) => setTextT({ ...textT, heading: e.target.value })} />
          </div>
          <div>
            <Label className="text-slate-100">Section subheading</Label>
            <Textarea data-testid="textt-subheading" className="mt-1.5 bg-slate-800 border-slate-700 text-white" rows={2} value={textT.subheading || ""} onChange={(e) => setTextT({ ...textT, subheading: e.target.value })} />
          </div>
        </Card>

        {(textT.items || []).map((r, i) => (
          <Card key={r.id || i} className="bg-slate-900 border-slate-800 p-5" data-testid={`textt-item-${i}`}>
            <div className="flex gap-4">
              <div className="w-24 shrink-0">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center">
                  {r.image_url ? <img src={mediaUrl(r.image_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-500">No image</span>}
                </div>
                <input ref={(el) => (fileRefs.current[`tt-${i}`] = el)} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFor(`tt-${i}`, e.target.files[0], (url) => listSet(textT, setTextT, i, { image_url: url }))} data-testid={`textt-file-${i}`} />
                <Button type="button" variant="outline" className="mt-2 w-20 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs px-1" onClick={() => fileRefs.current[`tt-${i}`]?.click()} data-testid={`textt-upload-${i}`}>
                  <Upload className="w-3.5 h-3.5 mr-1" /> {uploadingKey === `tt-${i}` ? "…" : "Photo"}
                </Button>
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Review {i + 1}</span>
                  <button type="button" onClick={() => listRemove(textT, setTextT, i)} className="text-red-400 hover:text-red-300 text-sm inline-flex items-center gap-1" data-testid={`textt-remove-${i}`}><Trash2 className="w-4 h-4" /> Remove</button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-100">Name / title</Label>
                    <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white" value={r.title || ""} onChange={(e) => listSet(textT, setTextT, i, { title: e.target.value })} data-testid={`textt-title-${i}`} />
                  </div>
                  <div>
                    <Label className="text-slate-100">Rating</Label>
                    <div className="mt-2 flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button key={s} type="button" onClick={() => listSet(textT, setTextT, i, { rating: s })} data-testid={`textt-star-${i}-${s}`}>
                          <Star className={`w-6 h-6 ${s <= (Number(r.rating) || 0) ? "text-amber-400 fill-amber-400" : "text-slate-600"}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-100">Review text</Label>
                  <Textarea className="mt-1.5 bg-slate-800 border-slate-700 text-white" rows={3} value={r.text || ""} onChange={(e) => listSet(textT, setTextT, i, { text: e.target.value })} data-testid={`textt-text-${i}`} />
                </div>
                <div>
                  <Label className="text-slate-100">Image URL <span className="text-slate-500 font-normal">(or use Photo upload)</span></Label>
                  <Input className="mt-1.5 bg-slate-800 border-slate-700 text-white font-mono-nums text-xs" value={r.image_url || ""} onChange={(e) => listSet(textT, setTextT, i, { image_url: e.target.value })} data-testid={`textt-imgurl-${i}`} />
                </div>
              </div>
            </div>
          </Card>
        ))}
        <Button type="button" variant="outline" onClick={() => listAdd(textT, setTextT, emptyReview)} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" data-testid="textt-add">
          <Plus className="w-4 h-4 mr-1.5" /> Add review
        </Button>
      </section>
      </>
      ) : null}

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="landing-save-bottom" className="bg-indigo-500 hover:bg-indigo-600 shadow-lg">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
